import React, { FC, useContext, useEffect, useMemo, useState } from 'react'
import { Flex, Typography, Avatar, Space, Spin, Button, Popover } from 'antd'
import { CalculatorOutlined, EnvironmentOutlined, MailOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { VerticalContext } from 'store/verticals/Context'
import { selectCurrentZone } from 'store/global'
import type { CurrentZone } from 'utils/types'
import { NetworkGraphButton } from 'components/network-graph/NetworkGraphButton'
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs'
import { NOT_AVAILABLE } from 'utils/constants'
import type { STREntityResponse } from '../types'
import {
  sumValues,
  buildOverviewChartData,
  buildListingMarkers,
  buildListingRows,
  buildHostSocials,
  getBusinessSourceIcon,
} from '../utils'
import Details from '../Details/Details'
import HostProfileDrawer from '../HostProfileDrawer/HostProfileDrawer'

const { Text, Title } = Typography

const LABEL_STYLE: React.CSSProperties = {
  fontFamily: 'Roboto',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '22px',
}

const VALUE_STYLE: React.CSSProperties = {
  ...LABEL_STYLE,
}

const SOCIAL_ICON_STYLE: React.CSSProperties = {
  width: 14,
  height: 14,
}

interface ContactListProps {
  label: string
  items: string[]
  hrefScheme: 'tel' | 'mailto'
}

const ContactList: FC<ContactListProps> = ({ label, items, hrefScheme }) => {
  if (items.length === 0) return null
  const [first, ...rest] = items
  const extraCount = rest.length

  return (
    <Flex align='center' gap={4}>
      <Text style={VALUE_STYLE}>
        <a href={`${hrefScheme}:${first}`}>{first}</a>
      </Text>
      {extraCount > 0 && (
        <Popover
          mouseLeaveDelay={0.3}
          placement='right'
          styles={{ body: { padding: 0 } }}
          title={
            <div
              style={{
                fontFamily: 'Roboto',
                fontWeight: 500,
                fontSize: 16,
                lineHeight: '24px',
                color: 'rgba(0, 0, 0, 0.85)',
                padding: '5px 16px',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              {label}
            </div>
          }
          content={
            <Flex vertical style={{ padding: '12px 16px' }}>
              {items.map(item => (
                <a
                  key={item}
                  href={`${hrefScheme}:${item}`}
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: 14,
                    lineHeight: '22px',
                    color: 'rgba(0, 0, 0, 0.85)',
                    wordBreak: 'break-all',
                  }}
                >
                  {item}
                </a>
              ))}
            </Flex>
          }
        >
          <Text type='secondary' style={{ ...LABEL_STYLE, cursor: 'default' }}>
            +{extraCount}
          </Text>
        </Popover>
      )}
    </Flex>
  )
}

interface VerticalPageProps {
  changeCurrentZoneIndexes?: (indexes: { baseIndices?: string; activityIndices?: string }) => void
}

const VerticalPage: FC<VerticalPageProps> = ({ changeCurrentZoneIndexes }) => {
  const { entityId } = useParams<{ entityId: string }>()
  const { getVerticalEntity, verticalEntity } = useContext(VerticalContext)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentZone: CurrentZone = useSelector(selectCurrentZone as any)

  useEffect(() => {
    changeCurrentZoneIndexes?.({
      baseIndices: currentZone?.strIndices,
      activityIndices: currentZone?.strActivityIndices,
    })
  }, [currentZone]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (entityId) {
      getVerticalEntity(entityId, currentZone?.strIndices)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, currentZone?.strIndices])

  const entity = verticalEntity as unknown as STREntityResponse | null

  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)

  const socials = useMemo(() => buildHostSocials(entity?.entityDetails?.profileUrls), [entity])
  const byYear = entity?.totals?.byYear
  const sources = useMemo(() => entity?.revenueSources ?? [], [entity])
  const totalRevenue = useMemo(() => sumValues(byYear?.total ?? byYear?.documented), [byYear])
  const unreportedRevenue = useMemo(() => sumValues(byYear?.unReportedByYear), [byYear])
  const overviewChartData = useMemo(() => (byYear ? buildOverviewChartData(byYear) : []), [byYear])
  const listingMarkers = useMemo(() => buildListingMarkers(sources), [sources])
  const listingRows = useMemo(() => buildListingRows(sources), [sources])

  const listingStats = useMemo(() => {
    const totalExp = entity?.totals?.totalExpenses ?? sumValues(byYear?.expenses)
    const totalRev = entity?.totals?.totalRevenue ?? totalRevenue
    const ratio = totalRev > 0 ? totalExp / totalRev : null

    return {
      totalListings: entity?.totals?.totalListings ?? 0,
      activeListings: entity?.totals?.activeListings ?? 0,
      estimatedRevenue: entity?.totals?.estimatedRevenue ?? 0,
      reportedRevenue: sumValues(byYear?.reportedByYear) || null,
      expensesRatio: ratio,
      reportedExpenses: totalExp || null,
    }
  }, [byYear, entity?.totals, totalRevenue])

  if (!entity) {
    return (
      <Flex justify='center' align='center' style={{ height: '60vh' }}>
        <Spin size='large' />
      </Flex>
    )
  }

  const mainAddress = entity.entityDetails?.location
  const phones = entity.entityDetails?.phones ?? []
  const emails = entity.entityDetails?.emails ?? []
  const taxpayerType = entity.overviewDetails?.activityType

  return (
    <div style={{ padding: '16px 24px 64px 24px' }}>
      <Breadcrumbs
        data={[
          { label: 'Short Term Rentals', href: '/short-term-rentals' },
          { label: entity.entityDetails?.name || NOT_AVAILABLE },
        ]}
        containerSx={{ marginBottom: 1 }}
      />
      <Flex
        align='center'
        justify='space-between'
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: '12px 20px',
          marginBottom: 16,
          border: '1px solid #f0f0f0',
        }}
      >
        <Flex align='center' gap={16}>
          <Avatar src={entity.entityDetails.image} size={48} />
          <div>
            <Flex align='center' gap={8}>
              <Title level={5} style={{ margin: 0 }}>
                {entity.entityDetails.name}
              </Title>
              {String((entity.entityDetails as Record<string, unknown>).tin || '') && (
                <Text type='secondary' style={LABEL_STYLE}>
                  | TIN: {String((entity.entityDetails as Record<string, unknown>).tin)}
                </Text>
              )}
            </Flex>
            <Flex align='center' gap={16} style={{ marginTop: 4 }}>
              {mainAddress && (
                <Flex align='center' gap={4}>
                  <EnvironmentOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                  <Text type='secondary' style={LABEL_STYLE}>
                    Location
                  </Text>
                  <Text style={VALUE_STYLE}>{mainAddress}</Text>
                </Flex>
              )}
              {taxpayerType && (
                <Flex align='center' gap={4}>
                  <CalculatorOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                  <Text type='secondary' style={LABEL_STYLE}>
                    Taxpayer Type
                  </Text>
                  <Text style={VALUE_STYLE}>{taxpayerType}</Text>
                </Flex>
              )}
              {phones.length > 0 && (
                <Flex align='center' gap={4}>
                  <PhoneOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                  <Text type='secondary' style={LABEL_STYLE}>
                    Phone
                  </Text>
                  <ContactList label='Phone' items={phones} hrefScheme='tel' />
                </Flex>
              )}
              {emails.length > 0 && (
                <Flex align='center' gap={4}>
                  <MailOutlined style={{ color: '#8c8c8c', fontSize: 14 }} />
                  <Text type='secondary' style={LABEL_STYLE}>
                    Email
                  </Text>
                  <ContactList label='Email' items={emails} hrefScheme='mailto' />
                </Flex>
              )}
              {socials.length > 0 && (
                <Flex align='center' gap={8}>
                  <Text type='secondary' style={LABEL_STYLE}>
                    Links
                  </Text>
                  <Space size={4}>
                    {socials.map(s => {
                      const iconSrc = getBusinessSourceIcon(s.platform)
                      if (!iconSrc) return null
                      return (
                        <a
                          key={s.platform}
                          href={s.url}
                          target='_blank'
                          rel='noreferrer'
                          style={{ display: 'inline-flex' }}
                        >
                          <img src={iconSrc} alt={s.platform} style={SOCIAL_ICON_STYLE} />
                        </a>
                      )
                    })}
                  </Space>
                </Flex>
              )}
            </Flex>
          </div>
        </Flex>

        <Flex vertical gap={8}>
          <Button icon={<UserOutlined />} onClick={() => setProfileDrawerOpen(true)}>
            Host Profile Info
          </Button>
          <NetworkGraphButton />
        </Flex>
      </Flex>
      <Details
        entityId={entityId}
        totalRevenue={totalRevenue}
        unreportedRevenue={unreportedRevenue}
        overviewChartData={overviewChartData}
        listingStats={listingStats}
        listingMarkers={listingMarkers}
        listingRows={listingRows}
        activityIndex={currentZone?.strActivityIndices}
        byYear={byYear}
        revenueSources={sources}
        contactInfo={entity.contactInfo}
        reportedStatus={entity.entityDetails?.reportedStatus}
      />
      <HostProfileDrawer
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
        hosts={entity.overviewDetails?.hosts}
      />
    </div>
  )
}

export default VerticalPage
